import { useEffect } from "react";
import { CheckCircle2, ChevronRight, Loader2, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { useQrLogin } from "@/lib/qr-login";

export function QrLoginDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { state, start, cancel } = useQrLogin();

    useEffect(() => {
        if (open) void start();
    }, [open, start]);

    if (!open) return null;

    function handleClose() {
        cancel();
        onClose();
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
            <div role="dialog" aria-modal="true" className="w-full max-w-sm rounded-xl bg-surface shadow-xl">
                <div className="flex items-center justify-between border-b border-line px-5 py-4">
                    <h2 className="text-base font-semibold text-ink">Thêm tài khoản Zalo</h2>
                    <button onClick={handleClose} aria-label="Đóng" className="text-ink-muted hover:text-ink">
                        <X className="size-4" />
                    </button>
                </div>

                <div className="space-y-4 px-5 py-5">
                    {(state.phase === "starting" || (state.phase === "waiting" && !state.qrImage)) && (
                        <div className="flex h-56 items-center justify-center">
                            <Loader2 className="size-6 animate-spin text-ink-muted" aria-label="Đang tạo mã" />
                        </div>
                    )}

                    {state.phase === "waiting" && state.qrImage && (
                        <>
                            <img
                                src={`data:image/png;base64,${state.qrImage}`}
                                alt="Mã QR đăng nhập Zalo"
                                className="mx-auto size-56 rounded-lg border border-line"
                            />
                            <p className="flex flex-wrap items-center justify-center gap-1 text-center text-sm text-ink-muted">
                                Mở Zalo trên điện thoại
                                <ChevronRight className="size-3.5 shrink-0" aria-hidden />
                                <span className="text-ink">Thêm</span>
                                <ChevronRight className="size-3.5 shrink-0" aria-hidden />
                                <span className="text-ink">Quét mã QR</span>
                            </p>
                        </>
                    )}

                    {state.phase === "scanned" && (
                        <div className="flex h-56 flex-col items-center justify-center gap-3">
                            {state.scannedAvatar && (
                                <img src={state.scannedAvatar} alt="" className="size-16 rounded-full" />
                            )}
                            <p className="text-sm text-ink">{state.scannedName}</p>
                            <p className="flex items-center gap-2 text-sm text-ink-muted">
                                <Loader2 className="size-4 animate-spin" />
                                Xác nhận trên điện thoại để hoàn tất
                            </p>
                        </div>
                    )}

                    {state.phase === "success" && (
                        <div className="flex h-56 flex-col items-center justify-center gap-3">
                            <CheckCircle2 className="size-10 text-success" />
                            <p className="text-sm text-ink">Đã thêm {state.message}</p>
                            <p className="text-xs text-ink-muted">Đang đồng bộ danh sách nhóm…</p>
                        </div>
                    )}

                    {(state.phase === "expired" || state.phase === "declined" || state.phase === "error") && (
                        <div className="space-y-3">
                            <Alert tone={state.phase === "error" ? "danger" : "warning"}>{state.message}</Alert>
                            <Button variant="secondary" className="w-full" onClick={() => void start()}>
                                <RefreshCw className="size-4" />
                                Lấy mã mới
                            </Button>
                        </div>
                    )}

                    <Button variant={state.phase === "success" ? "primary" : "ghost"} className="w-full" onClick={handleClose}>
                        {state.phase === "success" ? "Xong" : "Huỷ"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
